import { Link, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Clock,
  Compass,
  FilePlus2,
  GitBranch,
  Layers,
  Loader2,
  PencilRuler,
  Plus,
  Rocket,
  Search,
  Timer,
  Users,
} from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { createApp, listApps, type AppRecord } from "@/services/apps";
import {
  getDebugSchema,
  installTemplate,
  listTemplates,
  type AppTemplate,
  type DebugSchemaDetail,
} from "@/services/debugpavilion";
import { changeTypeLabel, formatVersionTime } from "@/lib/builder/version-history";
import { createDefaultApp } from "@/lib/builder/schema";
import { getAppAnalyticsSummary, type AppAnalyticsSummary } from "@/lib/analytics";

/**
 * 应用管理台（Wave 4 R3 / T4.7，ARCHITECTURE_DESIGN.md §5.6"管理"环节）。
 *
 * - 应用卡片：名称 / 状态（draft|active）/ 版本 / 更新时间 / 版本数 / 最近变更摘要；
 * - 创建：空白应用 或 从模板安装（GET /debugpavilion/templates → install）；
 * - 搜索过滤；进入编辑跳转 /builder?app=<id>；
 * - 数据分析（PV/UV/停留时长）：真实埋点，调 summary API 展示所选应用近 7 天
 *   数据（PV/UV/平均停留时长 + 按日曲线），无数据显示空态。
 */

interface AppCardSummary {
  detail: DebugSchemaDetail | null;
}

function StatusBadge({ status }: { status: string }) {
  if (status === "active") {
    return (
      <Badge className="gap-1 bg-green-600 hover:bg-green-600" data-testid="status-active">
        <Rocket className="h-3 w-3" />
        已发布
      </Badge>
    );
  }
  return (
    <Badge variant="outline" data-testid="status-draft">
      草稿
    </Badge>
  );
}

function formatUpdatedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function AnalyticsMetric({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-bold" data-testid="analytics-metric-value">
        {value}
      </p>
    </div>
  );
}

function AnalyticsDailyCurve({ daily }: { daily: { date: string; pv: number }[] }) {
  const max = Math.max(1, ...daily.map((d) => d.pv));
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="mb-3 text-sm font-medium">近 {daily.length} 日 PV 趋势</p>
      <div className="flex h-28 items-end gap-1.5" data-testid="analytics-daily-curve">
        {daily.map((d) => (
          <div
            key={d.date}
            className="flex min-w-0 flex-1 flex-col items-center gap-1"
            title={`${d.date}: ${d.pv} PV`}
          >
            <div
              className="w-full rounded-t bg-indigo-600/80"
              style={{ height: `${Math.max(d.pv > 0 ? 6 : 1, (d.pv / max) * 96)}px` }}
            />
            <span className="text-[10px] text-muted-foreground">{d.date.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [apps, setApps] = React.useState<AppRecord[]>([]);
  const [summaries, setSummaries] = React.useState<Record<string, AppCardSummary>>({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");
  const [templateOpen, setTemplateOpen] = React.useState(false);
  const [templates, setTemplates] = React.useState<AppTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = React.useState(false);
  const [templatesError, setTemplatesError] = React.useState<string | null>(null);
  const [installing, setInstalling] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  // 数据分析（真实埋点）：按应用查看 PV/UV/平均停留时长 + 7 日曲线
  const [analyticsAppId, setAnalyticsAppId] = React.useState<string | null>(null);
  const [analyticsSummary, setAnalyticsSummary] = React.useState<AppAnalyticsSummary | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = React.useState(false);

  const loadApps = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const records = await listApps();
      setApps(records);
      // 逐应用拉取版本摘要（版本数 + 最近变更）；失败不影响列表渲染
      const detailMap: Record<string, AppCardSummary> = {};
      await Promise.all(
        records.map(async (app) => {
          try {
            detailMap[app.id] = { detail: await getDebugSchema(app.id) };
          } catch {
            detailMap[app.id] = { detail: null };
          }
        }),
      );
      setSummaries(detailMap);
    } catch (err) {
      setError(`应用列表加载失败：${String(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadApps();
  }, [loadApps]);

  // 数据分析：应用列表就绪后默认选中第一个应用
  React.useEffect(() => {
    if (apps.length > 0 && !analyticsAppId) {
      setAnalyticsAppId(apps[0].id);
    }
  }, [apps, analyticsAppId]);

  // 数据分析：拉取所选应用近 7 天 PV/UV 汇总
  React.useEffect(() => {
    if (!analyticsAppId) return;
    let cancelled = false;
    setAnalyticsLoading(true);
    void getAppAnalyticsSummary(analyticsAppId, 7).then((summary) => {
      if (cancelled) return;
      setAnalyticsSummary(summary);
      setAnalyticsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [analyticsAppId]);

  const openTemplateDialog = async () => {
    setTemplateOpen(true);
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      setTemplates(await listTemplates());
    } catch (err) {
      setTemplatesError(`模板列表加载失败：${String(err)}`);
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleCreateBlank = async () => {
    setCreating(true);
    try {
      const record = await createApp("未命名应用", createDefaultApp("dev-tenant"));
      navigate(`/builder?app=${record.id}`);
    } catch (err) {
      setError(`创建失败：${String(err)}`);
    } finally {
      setCreating(false);
    }
  };

  const handleInstallTemplate = async (template: AppTemplate) => {
    setInstalling(template.id);
    try {
      const detail = await installTemplate(template.id);
      setTemplateOpen(false);
      navigate(`/builder?app=${detail.id}`);
    } catch (err) {
      setTemplatesError(`模板安装失败：${String(err)}`);
    } finally {
      setInstalling(null);
    }
  };

  const term = query.trim().toLowerCase();
  const visibleApps = term
    ? apps.filter((a) => a.name.toLowerCase().includes(term) || a.status.includes(term))
    : apps;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-col gap-4 rounded-xl border bg-card/50 p-5 shadow-sm backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">应用管理</h1>
          <p className="text-sm text-muted-foreground">管理你的应用：创建、编辑、预览、发布与版本回溯。</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => void handleCreateBlank()} disabled={creating}>
            {creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
            空白创建
          </Button>
          <Button variant="outline" onClick={() => void openTemplateDialog()}>
            <FilePlus2 className="mr-1.5 h-4 w-4" />
            从模板创建
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="搜索应用名称..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9"
          data-testid="dashboard-search"
        />
      </div>

      {loading && apps.length === 0 && (
        // 生成列表加载态：卡片 Skeleton 占位
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="dashboard-loading">
          {["s1", "s2", "s3"].map((id) => (
            <div key={id} className="space-y-3 rounded-lg border p-6">
              <Skeleton className="h-5 w-1/2" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-16 w-full" />
            </div>
          ))}
        </div>
      )}
      {!loading && apps.length === 0 && (
        <div className="rounded-lg border border-dashed p-10 text-center">
          <p className="text-sm text-muted-foreground">还没有应用，点击「空白创建」或「从模板创建」开始。</p>
          <Button asChild variant="outline" size="sm" className="mt-4" data-testid="dashboard-empty-guide">
            <Link to="/guide">
              <Compass className="mr-1.5 h-4 w-4" />
              查看快速开始
            </Link>
          </Button>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="dashboard-app-grid">
        {visibleApps.map((app) => {
          const detail = summaries[app.id]?.detail;
          const versionCount = detail?.versions?.length ?? null;
          const latest = detail?.versions?.[0];
          return (
            <Card key={app.id} data-app-id={app.id} className="flex flex-col transition-shadow hover:shadow-md">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Layers className="h-4 w-4 text-indigo-600" />
                    <span className="truncate">{app.name}</span>
                  </CardTitle>
                  <StatusBadge status={app.status} />
                </div>
                <CardDescription className="flex items-center gap-1 text-xs">
                  <Clock className="h-3 w-3" />
                  更新于 {formatUpdatedAt(app.updated_at)}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-md border bg-muted/40 p-2">
                    <p className="text-[10px] uppercase text-muted-foreground">版本</p>
                    <p className="font-mono text-xs font-semibold">{app.version}</p>
                  </div>
                  <div className="rounded-md border bg-muted/40 p-2">
                    <p className="text-[10px] uppercase text-muted-foreground">版本数</p>
                    <p className="font-mono text-xs font-semibold" data-testid={`version-count-${app.id}`}>
                      {versionCount === null ? "—" : versionCount}
                    </p>
                  </div>
                </div>
                <div className="rounded-md border border-dashed p-2 text-xs text-muted-foreground">
                  <span className="mb-1 flex items-center gap-1 font-medium">
                    <GitBranch className="h-3 w-3" />
                    最近变更
                  </span>
                  {latest ? (
                    <>
                      v{latest.version} · {changeTypeLabel(latest.change_type)} · {formatVersionTime(latest.created_at)}
                      {latest.change_log && <p className="mt-0.5 truncate" title={latest.change_log}>{latest.change_log}</p>}
                    </>
                  ) : (
                    "暂无版本记录"
                  )}
                </div>
                <div className="mt-auto flex gap-2">
                  <Button size="sm" className="flex-1" data-testid={`edit-app-${app.id}`} onClick={() => navigate(`/builder?app=${app.id}`)}>
                    <PencilRuler className="mr-1 h-3.5 w-3.5" />
                    进入编辑
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/run/${app.id}`)}>
                    <BarChart3 className="mr-1 h-3.5 w-3.5" />
                    运行
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* 数据分析：真实埋点（生命周期"数据分析"环节，PV/UV/停留时长），无数据显示空态 */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 className="h-4 w-4 text-indigo-600" />
              数据分析
            </CardTitle>
            <select
              value={analyticsAppId ?? ""}
              onChange={(e) => setAnalyticsAppId(e.target.value)}
              data-testid="analytics-app-select"
              className="h-8 max-w-56 rounded-md border bg-background px-2 text-xs"
            >
              {apps.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <CardDescription>页面访问量、访客与停留时长分析（近 7 天）。</CardDescription>
        </CardHeader>
        <CardContent data-testid="dashboard-analytics">
          {analyticsLoading ? (
            <div className="flex items-center gap-2 rounded-lg border border-dashed p-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              加载分析数据...
            </div>
          ) : analyticsSummary && analyticsSummary.total_pv > 0 ? (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <AnalyticsMetric icon={Users} label="PV（页面访问量）" value={String(analyticsSummary.total_pv)} />
                <AnalyticsMetric icon={Users} label="UV（独立访客）" value={String(analyticsSummary.total_uv)} />
                <AnalyticsMetric icon={Timer} label="平均停留时长" value={`${analyticsSummary.avg_duration_seconds.toFixed(1)}s`} />
              </div>
              <AnalyticsDailyCurve daily={analyticsSummary.daily} />
            </div>
          ) : (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="analytics-empty">
              暂无埋点数据——运行应用（/run/...）后自动采集 PV/UV 与停留时长。
            </div>
          )}
        </CardContent>
      </Card>

      {/* 从模板创建 */}
      <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FilePlus2 className="h-5 w-5 text-indigo-600" />
              从模板创建
            </DialogTitle>
            <DialogDescription>选择一个应用模板安装为新应用，安装后可在设计器中继续编辑。</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
            {templatesLoading && (
              <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                加载模板...
              </div>
            )}
            {templatesError && <p className="text-sm text-destructive">{templatesError}</p>}
            {!templatesLoading && !templatesError && templates.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">暂无可用模板。</p>
            )}
            {templates.map((template) => (
              <div key={template.id} data-template-id={template.id} className="flex items-center gap-3 rounded-lg border bg-card p-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{template.name}</span>
                    {template.category && (
                      <Badge variant="secondary" className="text-[10px]">
                        {template.category}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{template.description || "—"}</p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {template.download_count} 次安装 · {formatVersionTime(template.created_at)}
                  </p>
                </div>
                <Button
                  size="sm"
                  className="shrink-0"
                  data-testid={`install-${template.id}`}
                  disabled={installing !== null}
                  onClick={() => void handleInstallTemplate(template)}
                >
                  {installing === template.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1 h-3.5 w-3.5" />}
                  安装
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
